import { setMobileChatHistoryOpen, setMobileSidebarOpen } from '@/store/slices/ui-state.slice'
import AnonFeatureGuard from '@/components/guards/AnonFeatureGuard'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MenuHamburger } from '@/assets/icons'
import { AuthContext } from '@/context/auth-context'
import { Link, useMatch, useParams } from 'react-router-dom'
import { useContext } from 'react'
import { ClockRewindHistory, NewChat } from '@/assets/icons'
import NewChatMatterBoundButton from '@/chat-common/new_chat/components/NewChatMatterBoundButton'
import { IconButton } from '@/components/icon-button/IconButton'
import { useMatterOverview } from '@/matter/components/matter-overview/useMatterOverview'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { RootState } from '@/store/store'
import { routePatterns, kRouteSegments } from '@/routing/paths'
import { canCreateChat } from '@/matter/utils/matter-permissions'

/**
 * Dashboard Mobile Top Bar
 *
 * A mobile-only top bar which exposes UI controls to toggle components that are persistently visible
 * on desktop but hidden by default on small screens (e.g. the sidebar and chat history).
 */
export default function DashboardMobileTopBar() {
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)
  const { chatFeature, chatId } = useParams()
  const { matter, handleNewChat } = useMatterOverview()
  const { logoUrl } = authContext

  const validChatFeature: boolean = chatFeature === kRouteSegments.assistant
  const mattersInitialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  // Show chat controls only for valid chat features, and hide them on the unassigned-matter empty state (no chatId after matters finish loading)
  const showChatControls = validChatFeature && (!!chatId || (mattersInitialLoadComplete && !isUnassignedMatter(matter)))
  const isMatterOverview = !!useMatch(routePatterns.matterOverview)

  return (
    <>
      <div className="h-[60px] w-full top-0 z-40 px-3 py-[10px] flex items-center bg-brand-neutral-50 pr-3 border-b-[0.5px] border-brand-neutral-200">
        {/* Mobile Sidebar Toggle */}
        <button
          type="button"
          className="flex items-center justify-center h-8 w-8 p-1 m-[4px] text-brand-neutral-600 hover:bg-brand-neutral-200 rounded-full"
          onClick={() => dispatch(setMobileSidebarOpen(true))}
        >
          <span className="sr-only">Open sidebar</span>
          <MenuHamburger className="h-6 w-6" aria-hidden="true" />
        </button>

        <div className="w-full flex items-center justify-between text-sm font-medium ml-3 h-10">
          <div className="flex gap-x-3 items-center">
            {/* Logo that links to the dashboard */}
            <div className="text-base font-extrabold tracking-tighter">
              <Link to={'/dashboard'}>
                <img className="h-8" src={logoUrl} alt="Your legal AI assistant" />
              </Link>
            </div>
          </div>

          <div className="flex items-center">
            {showChatControls && (
              <AnonFeatureGuard showAuthOnGuard={false}>
                {/* Chat History Toggle */}
                <IconButton
                  icon={<ClockRewindHistory />}
                  aria-label="Open chat history"
                  title="History"
                  variant="plain"
                  color="neutral"
                  size="lg"
                  onClick={() => dispatch(setMobileChatHistoryOpen(true))}
                />

                {/* New Chat Button */}
                <NewChatMatterBoundButton iconOnly />
              </AnonFeatureGuard>
            )}

            {isMatterOverview && (
              <IconButton
                aria-label="New chat"
                color="primary"
                disabled={!matter || isUnassignedMatter(matter) || !canCreateChat(matter)}
                icon={<NewChat />}
                onClick={handleNewChat}
                variant="plain"
                size="lg"
              />
            )}

          </div>
        </div>
      </div>
    </>
  )
}
